import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getRiskScore, carryForward } from '../../api/risk';
import { useCompanyStore } from '../../stores/companyStore';
import { useToast } from '../shared/ToastProvider';

const BAND_COLOR: Record<'low' | 'medium' | 'high', { bg: string; fg: string; label: string }> = {
  low:    { bg: '#DCFCE7', fg: '#166534', label: 'נמוך' },
  medium: { bg: '#FEF3C7', fg: '#92400E', label: 'בינוני' },
  high:   { bg: '#FEE2E2', fg: '#991B1B', label: 'גבוה' },
};

/** Small risk-score pill in the top bar next to CompanyStatusChip.
 * Tooltip shows the components ("size +25 · freshness +0 · …"); click
 * opens a menu with the carry-forward action (copy last year's
 * working papers into the new year). */
export default function RiskBadge() {
  const selectedId = useCompanyStore((s) => s.selectedCompanyId);
  const qc = useQueryClient();
  const toast = useToast();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const currentYear = new Date().getFullYear();

  const { data: risk } = useQuery({
    queryKey: ['risk', 'score', selectedId],
    queryFn: getRiskScore,
    enabled: !!selectedId,
    staleTime: 60_000,
  });

  const cfMut = useMutation({
    mutationFn: () => carryForward(currentYear - 1, currentYear),
    onSuccess: (data) => {
      toast.success(
        `הועברו ${data.copied_papers} ניירות עבודה`,
        { description: `מ-${currentYear - 1} ל-${currentYear}` },
      );
      qc.invalidateQueries({ queryKey: ['activity'] });
      setAnchor(null);
    },
    onError: (e: any) => toast.error(e?.message ?? 'ההעברה נכשלה'),
  });

  if (!selectedId || !risk) return null;
  const c = BAND_COLOR[risk.band];

  return (
    <>
      <Tooltip title={`ציון סיכון ${risk.score}/100 · ${risk.explanation}`} arrow>
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 11,
            fontWeight: 700,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: c.bg,
            color: c.fg,
            border: '1px solid',
            borderColor: 'transparent',
            transition: 'border-color .15s',
            '&:hover': { borderColor: c.fg },
            userSelect: 'none',
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 14 }} />
          <Box component="span">סיכון {c.label}</Box>
          <Box component="span" sx={{ opacity: 0.6 }}>{risk.score}</Box>
        </Box>
      </Tooltip>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 280 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>ציון סיכון: {risk.score}/100</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
            {risk.explanation}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          disabled={cfMut.isPending}
          onClick={() => {
            if (window.confirm(`להעתיק ניירות עבודה מ-${currentYear - 1} ל-${currentYear}?`)) {
              cfMut.mutate();
            }
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              העברה מקדמית מ-{currentYear - 1}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              העתק ניירות עבודה מהשנה שעברה לשנה הנוכחית
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}
